<script setup lang="ts">
import type { HTMLAttributes } from "vue";
import { cn } from "@/lib/utils";

const props = withDefaults(
    defineProps<{
        tag?: keyof HTMLElementTagNameMap | string;
        fluid?: boolean;
        center?: boolean;
        padded?: boolean;
        padding?: "none" | "sm" | "md" | "lg" | "xl" | "default" | string;
        max?: "none" | "sm" | "md" | "lg" | "xl" | "2xl" | "3xl" | "screen-sm" | "screen-md" | "screen-lg" | "screen-xl" | "screen-2xl";
        class?: HTMLAttributes["class"];
        height?: "none" | "screen" | "fill" | "with-header" | "with-footer" | "with-header-footer";
    }>(),
    {
        class: "",
        tag: "div",
        fluid: false,
        center: true,
        padded: true,
        padding: "default",
        max: "screen-xl",
        height: "fill",
    }
);

const maxClass = computed(() => {
    if (props.fluid) return "max-w-none";
    const map: Record<NonNullable<typeof props.max>, string> = {
        none: "max-w-none",
        sm: "max-w-sm",
        md: "max-w-md",
        lg: "max-w-lg",
        xl: "max-w-xl",
        "2xl": "max-w-2xl",
        "3xl": "max-w-3xl",
        "screen-sm": "max-w-screen-sm",
        "screen-md": "max-w-screen-md",
        "screen-lg": "max-w-screen-lg",
        "screen-xl": "max-w-screen-xl",
        "screen-2xl": "max-w-screen-2xl",
    };
    return map[props.max ?? "screen-xl"];
});

const paddingClass = computed(() => {
    if (!props.padded || props.padding === "none") return "";
    const preset: Record<string, string> = {
        default: "px-4 sm:px-6 lg:px-8",
        sm: "px-3 sm:px-4 md:px-5",
        md: "px-4 sm:px-6 lg:px-8",
        lg: "px-6 sm:px-8 lg:px-10",
        xl: "px-8 sm:px-10 lg:px-12",
    };
    const key = typeof props.padding === "string" ? props.padding : "default";

    return preset[key] ?? key;
});

const heightClass = computed(() => {
    const map: Record<NonNullable<typeof props.height>, string> = {
        none: "",
        fill: "min-h-screen-fill",
        screen: "min-h-screen",
        "with-header": "min-h-screen-with-header",
        "with-footer": "min-h-screen-with-footer",
        "with-header-footer": "min-h-screen-with-header-footer",
    };
    return map[props.height ?? "fill"];
});

const classes = computed(() => cn("w-full", props.center ? "mx-auto" : "", maxClass.value, paddingClass.value, heightClass.value, props.class));
</script>

<template>
    <component :is="props.tag" data-slot="container" :data-variant="props.fluid ? 'fluid' : 'fixed'" :class="classes">
        <slot />
    </component>
</template>

<style></style>
<style></style>
